import type seedrandom from 'seedrandom';
import type { CssUnit } from '../css';

export interface CssRngFn {
    (unit?: CssUnit, decimals?: number): string;
    (max: number, unit?: CssUnit, decimals?: number): string;
    (min: number, max: number, unit?: CssUnit, decimals?: number): string;
}

export const defaultCssBounds: Record<CssUnit, [number, number]> = {
    '%': [0, 100],
    ch: [0, 40],
    rem: [0, 20],
    ex: [0, 8],
    em: [0, 20],
    vw: [0, 100],
    vh: [0, 100],
    vmin: [0, 100],
    vmax: [0, 100],
    cm: [0, (800 * 2.54) / 96],
    mm: [0, (800 * 2.54 * 10) / 96],
    in: [0, 800 / 96],
    px: [0, 800],
    pt: [0, (800 * 72) / 96],
    pc: [0, (800 * 12 * 72) / 96],
};

export function makeCssRng(base: seedrandom.PRNG): CssRngFn {
    return ((arg0, arg1, arg2, arg3) => {
        let unit: CssUnit = '%';
        let [min, max] = defaultCssBounds[unit];
        let decimals = 0;

        if (typeof arg0 === 'string') {
            unit = arg0 as CssUnit;
            [min, max] = defaultCssBounds[unit];
            decimals = arg1 ?? decimals;
        } else if (typeof arg0 === 'number') {
            if (typeof arg1 === 'number') {
                min = arg0;
                max = arg1;
                unit = arg2 ?? unit;
                decimals = arg3 ?? decimals;
            } else {
                min = 0;
                max = arg0;
                unit = arg1 ?? unit;
                decimals = (arg2 as unknown as number | undefined) ?? decimals;
            }
        }

        const x = min + base() * max;
        const roundFactor = Math.pow(10, decimals);
        const rounded =
            Math.round((x + Number.EPSILON) * roundFactor) / roundFactor;
        return `${rounded}${unit}`;
    }) as CssRngFn;
}
